import { Head, Link, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Activity {
  id: number
  title: string
}
interface CustomerInfo {
  id: number
  name: string
}
interface Option {
  id: number
  name: string
}
interface Props {
  activity: Activity
  customer: CustomerInfo
  contact_roles: Option[]
  log_types: Option[]
  employees: Option[]
  errors?: Record<string, string[]>
}

export default function AddContact({
  activity,
  customer,
  contact_roles,
  log_types,
  employees,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Marketing Activities", href: "/marketing_activities" },
    { title: activity.title, href: `/marketing_activities/${activity.id}` },
    {
      title: "Attendees",
      href: `/marketing_activities/${activity.id}/list_items`,
    },
    {
      title: "Add Contact",
      href: `/marketing_activities/${activity.id}/list_items/add_contact?customer_id=${customer.id}`,
    },
  ]

  const { data, setData, post, processing } = useForm({
    customer_id: customer.id,
    contact: {
      salutation: "",
      first_name: "",
      middle_name: "",
      last_name: "",
      suffix: "",
      department: "",
      role: "",
      phone: "",
      cell_phone: "",
      email: "",
    },
    follow_up: {
      scheduled_date: "",
      log_type_id: "",
      user_id: "",
      scheduled_notes: "",
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/marketing_activities/${activity.id}/list_items/add_contact`)
  }

  const setContactField = (field: string, value: string) => {
    setData("contact", { ...data.contact, [field]: value })
  }

  const setFollowUpField = (field: string, value: string) => {
    setData("follow_up", { ...data.follow_up, [field]: value })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Add Contact for ${customer.name}`} />
      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">
          Add Contact for {customer.name}
        </h1>
        <form onSubmit={handleSubmit} className="max-w-3xl space-y-8">
          {/* Contact Section */}
          <fieldset className="border-hpi-light-gray space-y-4 rounded border p-4">
            <legend className="text-hpi-dark-blue px-2 font-semibold">
              Contact
            </legend>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium">Prefix</label>
                <select
                  value={data.contact.salutation}
                  onChange={(e) =>
                    setContactField("salutation", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                >
                  <option value="">Select...</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Ms">Ms</option>
                  <option value="Dr">Dr</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium">
                  First Name <span className="text-hpi-red">*</span>
                </label>
                <input
                  type="text"
                  value={data.contact.first_name}
                  onChange={(e) =>
                    setContactField("first_name", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                  required
                />
                {errors?.first_name && (
                  <p className="text-hpi-red text-sm">
                    {errors.first_name.join(", ")}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium">Middle Name</label>
                <input
                  type="text"
                  value={data.contact.middle_name}
                  onChange={(e) =>
                    setContactField("middle_name", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Last Name</label>
                <input
                  type="text"
                  value={data.contact.last_name}
                  onChange={(e) => setContactField("last_name", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Suffix</label>
                <input
                  type="text"
                  value={data.contact.suffix}
                  onChange={(e) => setContactField("suffix", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={5}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Title / Department
                </label>
                <input
                  type="text"
                  value={data.contact.department}
                  onChange={(e) =>
                    setContactField("department", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Role</label>
                <select
                  value={data.contact.role}
                  onChange={(e) => setContactField("role", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                >
                  <option value="">Select...</option>
                  {contact_roles.map((cr) => (
                    <option key={cr.id} value={cr.id}>
                      {cr.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Office Phone
                </label>
                <input
                  type="tel"
                  value={data.contact.phone}
                  onChange={(e) => setContactField("phone", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Cell Phone</label>
                <input
                  type="tel"
                  value={data.contact.cell_phone}
                  onChange={(e) =>
                    setContactField("cell_phone", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Email</label>
                <input
                  type="email"
                  value={data.contact.email}
                  onChange={(e) => setContactField("email", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
            </div>
          </fieldset>

          {/* Next Action (Optional) */}
          <fieldset className="border-hpi-light-gray space-y-4 rounded border p-4">
            <legend className="text-hpi-dark-blue px-2 font-semibold">
              Next Action{" "}
              <span className="text-hpi-med-gray text-sm font-normal">
                (Optional)
              </span>
            </legend>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium">When</label>
                <input
                  type="date"
                  value={data.follow_up.scheduled_date}
                  onChange={(e) =>
                    setFollowUpField("scheduled_date", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">How</label>
                <select
                  value={data.follow_up.log_type_id}
                  onChange={(e) =>
                    setFollowUpField("log_type_id", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                >
                  <option value="">Select...</option>
                  {log_types.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      {lt.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium">Assign to</label>
                <select
                  value={data.follow_up.user_id}
                  onChange={(e) => setFollowUpField("user_id", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                >
                  <option value="">Manager</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium">
                  Describe what needs to be done
                </label>
                <textarea
                  value={data.follow_up.scheduled_notes}
                  onChange={(e) =>
                    setFollowUpField("scheduled_notes", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  rows={3}
                />
              </div>
            </div>
          </fieldset>

          {/* Actions */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Saving..." : "Add Contact"}
            </button>
            <Link
              href={`/marketing_activities/${activity.id}/list_items`}
              className="hover:bg-hpi-pale-gray rounded border px-4 py-2"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
